import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getRandomInsight, type Insight } from "@/data/elevateInsights";
import SkylineOverlay from "@/components/brand/SkylineOverlay";

const ElevateMomentSection = () => {
  const [insight, setInsight] = useState<Insight | null>(null);
  const [revealing, setRevealing] = useState(false);

  const reveal = () => {
    setRevealing(true);
    setTimeout(() => {
      setInsight((prev) => getRandomInsight(undefined, prev?.id));
      setRevealing(false);
    }, 350);
  };

  return (
    <section
      aria-labelledby="elevate-moment-heading"
      className="relative overflow-hidden bg-primary text-primary-foreground"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, hsl(var(--secondary)) 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />
      {/* Background layer: architecture pushed far back with blur + low opacity */}
      <SkylineOverlay className="opacity-[0.35] blur-[3px]" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-primary via-primary/85 to-transparent"
      />

      {/* Middle glow layer: separates card from background */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-secondary/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-primary/40 blur-3xl"
      />


      <div className="container-narrow relative section-padding py-20 md:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-primary-foreground/95">
            <Sparkles className="h-3.5 w-3.5 text-secondary" />
            Signature Experience
          </span>

          <h2
            id="elevate-moment-heading"
            className="mt-6 font-display text-4xl font-bold tracking-tight text-primary-foreground md:text-5xl"
          >
            Elevate Moment
          </h2>
          <p className="mt-4 text-lg text-primary-foreground/75">
            Take a pause. Refocus. Gain perspective.
          </p>

          <div className="relative mx-auto mt-10 max-w-2xl rounded-2xl border border-white/15 bg-primary/60 p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/5 backdrop-blur-md md:p-10">
            <div
              className={`min-h-[120px] flex items-center justify-center transition-all duration-500 ${
                revealing ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
              }`}
            >
              {insight ? (
                <div>
                  <p className="font-display text-xl italic leading-relaxed text-primary-foreground/95 md:text-2xl">
                    “{insight.text}”
                  </p>
                  <p className="mt-4 text-xs uppercase tracking-[0.25em] text-secondary">
                    {insight.category}
                  </p>
                </div>
              ) : (
                <p className="text-base text-primary-foreground/55">
                  One thought. One moment. Then back to building.
                </p>
              )}
            </div>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                size="lg"
                variant="secondary"
                onClick={reveal}
                className="min-w-[200px]"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {insight ? "Reveal Another" : "Reveal Insight"}
              </Button>
              <Link to="/elevate-moment">
                <Button
                  size="lg"
                  variant="ghost"
                  className="text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
                >
                  Explore the collection
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <p className="mt-6 text-xs uppercase tracking-[0.25em] text-primary-foreground/40">
            Mindset · Leadership · Clarity
          </p>
        </div>
      </div>
    </section>
  );
};

export default ElevateMomentSection;
